import React from "react";
import Flex from "../Flex/Flex";
import { Typography } from "../Typography";
import CommonFilter from "./CommonFilter";
import { useFiltersContext } from "./FiltersContext";
import { normalizeValue } from "./helpers";
import {
    ICommonFilterTypeProps,
    ICustomFilterProps,
    TFilterProps,
    TGetValueFunction,
    TSetValueFunction
} from "./types";

type TDestructuredFilterKeys<TQuery extends string> =
    | keyof ICommonFilterTypeProps<"filter">
    | Extract<keyof TFilterProps<TQuery>, "name">;

export const isCustomFilter = <TQuery extends string>(
    props: Omit<TFilterProps<TQuery>, TDestructuredFilterKeys<TQuery>>
): props is Omit<ICustomFilterProps<TQuery>, TDestructuredFilterKeys<TQuery>> => {
    return "render" in props;
};

const Filter = <TQuery extends string>({ title, name, ...props }: TFilterProps<TQuery>): JSX.Element => {
    const { query } = useFiltersContext<TQuery>();

    const getValue: TGetValueFunction<TQuery> = filterName => {
        const value: string[] | null = query.getAll(filterName);
        return normalizeValue(value);
    };
    const setValue: TSetValueFunction<TQuery> = (filterName, newValue) => {
        if (!newValue || !newValue.length) {
            query.del(filterName);
            return;
        }

        query.set(filterName, newValue);
    };

    const isCustom = isCustomFilter<TQuery>(props);

    return (
        <Flex column spacing={isCustom ? 1 : 2.5} width="100%">
            {title && (
                <Typography variant="label-small-regular" color="text-primary-500">
                    {title}
                </Typography>
            )}
            {isCustom ? (
                props.render(getValue, setValue)
            ) : (
                <CommonFilter<TQuery>
                    name={name}
                    onChange={newValue => setValue(name, newValue)}
                    value={getValue(name)}
                    {...props}
                />
            )}
        </Flex>
    );
};

export default Filter;
